import { DatasetItem, GridRow } from '../../../types/types';
import { ReportAlignment, ReportColumn } from '../types';
import { isNumericItemType } from './isNumericItemType';

/**
 * A template column joined with its definition item. The template only stores
 * the item reference, so label/type/format/formatItem always come from the
 * current definition — that is what keeps a report identical to the grid.
 */
export type ResolvedPrintColumn = {
  name: string;
  label: string;
  align: ReportAlignment;
  isNumeric: boolean;
  /** Shape formatValue expects. */
  formatConfig: {
    name: string;
    type?: string;
    format?: string;
    formatItem?: string;
  };
};

export const resolvePrintColumns = (
  columns: ReportColumn[],
  items: DatasetItem[]
): ResolvedPrintColumn[] =>
  columns.map((column) => {
    const item = items.find((candidate) => candidate.name === column.item);
    const isNumeric = isNumericItemType(item?.type);

    return {
      name: column.item,
      label: column.label ?? item?.label ?? column.item,
      align:
        column.align ?? item?.alignment ?? (isNumeric ? 'right' : 'left'),
      isNumeric,
      formatConfig: {
        name: column.item,
        type: item?.type,
        // A format on the template is an explicit override of the definition.
        format: column.format ?? item?.format,
        formatItem: item?.formatItem,
      },
    };
  });

/** Definition item behind a field reference, for label and format resolution. */
export const findDatasetItem = (
  items: DatasetItem[],
  name: string
): DatasetItem | undefined =>
  items.find((candidate) => candidate.name === name);

/** Rows always arrive as an array; narrow defensively for optional sources. */
export const asRows = (rows: GridRow[] | undefined): GridRow[] =>
  Array.isArray(rows) ? rows : [];
